import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import useTranslation from '@/hooks/useTranslation'
import usePageTitle from '@/hooks/usePageTitle'
import useRetailerApi from '@/useRetailerApi'
import { LANGUAGE_LOCALES } from '@/locales/languages'
import Button from '@/components/Button'
import OrderDetails from '@/components/OrderDetails'
import OrderStatusBadge from '@/components/OrderStatusBadge'

const PAGE_SIZE = 10

function Orders() {
    const { t, language } = useTranslation()
    usePageTitle(t('nav.orders'))
    const { fetchOrders } = useRetailerApi()
    const location = useLocation()

    const [orders, setOrders] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [showBanner, setShowBanner] = useState(false)

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        if (location.state?.fromCartSubmit || params.has('submitted')) {
            setShowBanner(true)
            const timer = setTimeout(() => setShowBanner(false), 5000)
            window.history.replaceState({}, '')
            return () => clearTimeout(timer)
        }
    }, [location.state])

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        fetchOrders(0, PAGE_SIZE)
            .then((data) => {
                if (cancelled) return
                setOrders(data.orders)
                setTotal(data.total)
                setLoading(false)
            })
            .catch(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [])

    const handleLoadMore = async () => {
        setLoadingMore(true)
        try {
            const data = await fetchOrders(orders.length, PAGE_SIZE)
            setOrders((prev) => [...prev, ...data.orders])
            setTotal(data.total)
        } finally {
            setLoadingMore(false)
        }
    }

    const formatPrice = (amount) =>
        Number(amount).toLocaleString(LANGUAGE_LOCALES[language], {
            style: 'currency',
            currency: 'EUR',
        })

    const formatDate = (dateStr) => {
        const d = new Date(dateStr)
        return d.toLocaleDateString(LANGUAGE_LOCALES[language], {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
        })
    }

    const hasMore = orders.length < total

    return (
        <div className="flex flex-col px-[24px] lg:px-[48px] py-[100px] lg:py-[172px] mx-auto">
            {showBanner && (
                <div className="mb-[24px] bg-[#2ECC71]/10 text-[#2ECC71] rounded-[8px] px-[20px] py-[14px] text-[14px] font-semibold">
                    {t('cart.submitSuccess')}
                </div>
            )}

            <div className="flex flex-col gap-[4px] mb-[40px]">
                <h1 className="text-[34px] lg:text-[40px] font-semibold uppercase">
                    {t('nav.orders')}
                </h1>
                {!loading && (
                    <span className="uppercase text-dark-blue-40 text-[14px]">
                        {t('orders.count', { count: total })}
                    </span>
                )}
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-[80px]">
                    <svg
                        className="animate-spin h-6 w-6 text-dark-blue/20"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                    </svg>
                </div>
            ) : orders.length === 0 ? (
                <p className="text-dark-blue-40 uppercase text-[14px]">
                    {t('orders.empty')}
                </p>
            ) : (
                <div className="flex flex-col">
                    {/* Desktop */}
                    <table className="hidden lg:table w-full border-collapse">
                        <thead>
                            <tr className="bg-[#F5F5F6] text-left">
                                <th className="text-[16px] font-semibold px-[24px] py-[12px] first:rounded-l-[8px]">
                                    {t('orders.date')}
                                </th>
                                <th className="text-[16px] font-semibold px-[12px] py-[12px]">
                                    {t('orders.orderNumber')}
                                </th>
                                <th className="text-[16px] font-semibold px-[12px] py-[12px]">
                                    {t('orders.items')}
                                </th>
                                <th className="text-[16px] font-semibold px-[12px] py-[12px]">
                                    {t('orders.priceHT')}
                                </th>
                                <th className="text-[16px] font-semibold px-[12px] py-[12px]">
                                    {t('orders.fulfillment')}
                                </th>
                                <th className="text-[16px] font-semibold px-[12px] py-[12px]">
                                    {t('orders.payment')}
                                </th>
                                <th className="px-[24px] py-[12px] last:rounded-r-[8px]"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order) => {
                                const amount = order.amounts?.total ?? 0

                                return (
                                    <tr
                                        key={order.id}
                                        className="border-b border-dark-blue/10"
                                    >
                                        <td className="text-[16px] px-[24px] py-[16px] whitespace-nowrap">
                                            {formatDate(order.date)}
                                        </td>
                                        <td className="text-[16px] text-dark-blue/40 px-[12px] py-[16px] whitespace-nowrap">
                                            {order.number}
                                        </td>
                                        <td className="text-[16px] px-[12px] py-[16px]">
                                            {order.item_count}
                                        </td>
                                        <td className="text-[16px] px-[12px] py-[16px] whitespace-nowrap">
                                            {formatPrice(amount)}
                                        </td>
                                        <td className="px-[12px] py-[16px]">
                                            <OrderStatusBadge
                                                type="fulfillment"
                                                status={
                                                    order.fulfillment_status
                                                }
                                            />
                                        </td>
                                        <td className="px-[12px] py-[16px]">
                                            <OrderStatusBadge
                                                type="payment"
                                                status={order.financial_status}
                                            />
                                        </td>
                                        <td className="px-[24px] py-[16px] flex justify-end">
                                            <OrderDetails
                                                order={order}
                                                formatDate={formatDate}
                                                formatPrice={formatPrice}
                                            />
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>

                    {/* Mobile */}
                    <div className="flex flex-col lg:hidden">
                        {orders.map((order, i) => {
                            const amount = order.amounts?.total ?? 0

                            return (
                                <div
                                    key={order.id}
                                    className={`flex flex-col gap-[8px] py-[16px] ${i > 0 ? 'border-t border-dark-blue/10' : ''}`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex flex-col gap-[4px]">
                                            <span className="text-[16px]">
                                                {formatDate(order.date)}
                                            </span>
                                            <span className="text-[16px] text-dark-blue/40">
                                                {order.number}
                                            </span>
                                        </div>
                                        <span className="text-[16px] font-semibold">
                                            {formatPrice(amount)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-[8px] flex-wrap">
                                        <span className="text-[16px] text-dark-blue/40">
                                            {t('orders.productCount', {
                                                count: order.item_count,
                                            })}
                                        </span>
                                        <OrderStatusBadge
                                            type="fulfillment"
                                            status={order.fulfillment_status}
                                        />
                                        <OrderStatusBadge
                                            type="payment"
                                            status={order.financial_status}
                                        />
                                    </div>
                                    <OrderDetails
                                        order={order}
                                        formatDate={formatDate}
                                        formatPrice={formatPrice}
                                    />
                                </div>
                            )
                        })}
                    </div>

                    {hasMore ? (
                        <div className="flex justify-center pt-[32px]">
                            <Button
                                variant="outline"
                                loading={loadingMore}
                                onClick={handleLoadMore}
                            >
                                {t('orders.loadMore')}
                            </Button>
                        </div>
                    ) : (
                        <p className="text-center text-dark-blue/40 text-[14px] pt-[32px]">
                            {t('orders.allLoaded')}
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}

export default Orders
